import { forwardRef } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { platformProps } from "../../lib";
import { TOOLTIP_ARROW } from "./arrow-size";
import type {
  TooltipArrowPrimitiveProps,
  TooltipContentPrimitiveProps,
  TooltipProviderPrimitiveProps,
  TooltipRootPrimitiveProps,
  TooltipTriggerPrimitiveProps,
} from "./types";

/** Ref types, resolved per platform like the components. */
export type TooltipTriggerRef = HTMLButtonElement;
export type TooltipContentRef = HTMLDivElement;
export type TooltipArrowRef = SVGSVGElement;

/**
 * Tooltip Provider - Web implementation (Radix)
 *
 * Wraps an arbitrary subtree of triggers/contents to share open/close timing.
 */
const TooltipProvider = ({
  delayDuration = 200,
  skipDelayDuration,
  disableHoverableContent,
  children,
}: TooltipProviderPrimitiveProps) => (
  <TooltipPrimitive.Provider
    delayDuration={delayDuration}
    skipDelayDuration={skipDelayDuration}
    disableHoverableContent={disableHoverableContent}
  >
    {children}
  </TooltipPrimitive.Provider>
);

TooltipProvider.displayName = TooltipPrimitive.Provider.displayName;

/**
 * Tooltip Root - Web implementation (Radix)
 *
 * Unstyled primitive. Styling is applied by the molecule layer via className.
 */
const TooltipRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  delayDuration,
  children,
}: TooltipRootPrimitiveProps) => (
  <TooltipPrimitive.Root
    open={open}
    defaultOpen={defaultOpen}
    onOpenChange={onOpenChange}
    delayDuration={delayDuration}
  >
    {children}
  </TooltipPrimitive.Root>
);

TooltipRoot.displayName = TooltipPrimitive.Root.displayName;

/**
 * Tooltip Trigger - Web implementation (Radix) *
 * Typed by its shared props only; what a parent's `asChild` Slot injects
 * (handlers, `disabled`) still passes through to the library.
 */
const TooltipTrigger = forwardRef<TooltipTriggerRef, TooltipTriggerPrimitiveProps>(
  ({ asChild, className, children, testID, accessibilityLabel, ...injected }, ref) => (
    <TooltipPrimitive.Trigger
      ref={ref}
      asChild={asChild}
      // Only when set: with asChild an undefined aria-label reaches the child's
      // props and can override the label it renders itself
      {...(accessibilityLabel !== undefined && { "aria-label": accessibilityLabel })}
      className={className}
      {...platformProps({ testID, slot: "tooltip-trigger" })}
      {...injected}
    >
      {children}
    </TooltipPrimitive.Trigger>
  ),
);

TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

/**
 * Tooltip Content - Web implementation (Radix)
 *
 * Wrapped in a Portal so positioning is isolated from parent overflow/clipping.
 */
const TooltipContent = forwardRef<TooltipContentRef, TooltipContentPrimitiveProps>(
  (
    {
      side = "top",
      align = "center",
      sideOffset = 8,
      alignOffset,
      className,
      children,
      testID,
      accessibilityLabel,
      ...injected
    },
    ref,
  ) => (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        aria-label={accessibilityLabel}
        className={className}
        {...platformProps({ testID, slot: "tooltip-content" })}
        {...injected}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  ),
);

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * Tooltip Arrow - Web implementation (Radix)
 *
 * Draws `TOOLTIP_ARROW`, Figma's rounded pointer, in place of Radix's
 * triangle; Radix still points it at the trigger and rotates it per side, and
 * adds its height to `sideOffset`. Pass a `fill-*` Tailwind class via
 * `className` to set the arrow color.
 *
 * The `side` and `align` props are accepted for cross-platform parity but
 * ignored on web — Radix derives them from the parent context.
 */
const TooltipArrow = forwardRef<TooltipArrowRef, TooltipArrowPrimitiveProps>(
  ({ side: _side, align: _align, className }, ref) => (
    <TooltipPrimitive.Arrow
      ref={ref}
      asChild
      width={TOOLTIP_ARROW.width}
      height={TOOLTIP_ARROW.height}
    >
      {/* Its viewBox replaces Radix's 30×10 one */}
      <svg
        viewBox={`0 0 ${TOOLTIP_ARROW.width} ${TOOLTIP_ARROW.height}`}
        preserveAspectRatio="none"
        className={className}
      >
        <path d={TOOLTIP_ARROW.path} />
      </svg>
    </TooltipPrimitive.Arrow>
  ),
);

TooltipArrow.displayName = TooltipPrimitive.Arrow.displayName;

export { TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent, TooltipArrow };
